/**
 * Proje geçmişi.
 *
 * Every line here is a timestamp the database already held for its own
 * reasons - a session's `created_at`, a coverage analysis' version row, a
 * job's `finished_at`. **Nothing writes an event.** There is no event table,
 * no `localStorage` history and no client-side log, which is why this cannot
 * drift from the data: there is only one copy of each fact and the timeline
 * is a projection of it.
 *
 * The sentence is built here rather than stored, so an event never reads two
 * different ways in two places and no Turkish string lives in a database.
 */

import { useCallback } from "react";
import { getProjectTimeline } from "@/api/client";
import type { TimelineEvent } from "@/api/types";
import { ErrorState } from "@/components/ErrorState";
import { EmptyState, Loading } from "@/components/ui";
import { formatDateTime } from "@/lib/format";
import { useAsync } from "@/lib/useAsync";
import "./workflow.css";

/** How many events one screen asks for. A history, not an audit log. */
const TIMELINE_LIMIT = 30;

/**
 * One event as a sentence.
 *
 * `count` is only ever interpolated where the backend measured one. Where an
 * event has no count the sentence has no number, rather than a zero.
 */
export function timelineText(event: TimelineEvent): string {
  switch (event.kind) {
    case "project_created":
      return "Proje oluşturuldu";
    case "capture_session_created":
      return "Çekim oturumu açıldı";
    case "capture_session_completed":
      return "Çekim oturumu tamamlandı";
    case "media_added":
      return `${event.count ?? 0} dosya eklendi`;
    case "analysis_completed":
      return "Analiz tamamlandı";
    case "coverage_completed":
      return event.version !== null
        ? `Kapsama değerlendirmesi tamamlandı (s${event.version})`
        : "Kapsama değerlendirmesi tamamlandı";
    case "videos_prepared":
      return `${event.count ?? 0} video rekonstrüksiyon için hazırlandı`;
    case "reconstruction_queued":
      return event.version !== null
        ? `Rekonstrüksiyon sıraya alındı (v${event.version})`
        : "Rekonstrüksiyon sıraya alındı";
    case "reconstruction_succeeded":
      // Deliberately "seyrek 3D", not "3D model": what a succeeded job proves
      // is a registered camera set and a point cloud, and this product has
      // been wrong about that distinction before.
      return event.version !== null
        ? `Seyrek 3D rekonstrüksiyon tamamlandı (v${event.version})`
        : "Seyrek 3D rekonstrüksiyon tamamlandı";
    case "reconstruction_failed":
      return event.version !== null
        ? `Rekonstrüksiyon başarısız oldu (v${event.version})`
        : "Rekonstrüksiyon başarısız oldu";
  }
}

export function ProjectTimeline({ projectId }: { projectId: string }) {
  const fetcher = useCallback(
    (signal: AbortSignal) =>
      getProjectTimeline(projectId, { limit: TIMELINE_LIMIT }, { signal }),
    [projectId],
  );
  const { state, reload } = useAsync(fetcher);

  if (state.status === "loading" && state.data === null) return <Loading />;
  if (state.status === "error") return <ErrorState error={state.error} onRetry={reload} />;

  const events = state.status === "success" ? state.data : [];

  // A project always has at least its own creation, so a genuinely empty list
  // means an older API. Say nothing happened rather than apologising.
  if (events.length === 0) {
    return <EmptyState title="Bu projede henüz etkinlik yok." />;
  }

  return (
    <ol className="timeline">
      {events.map((event, index) => (
        <li className="timeline__item" key={`${event.kind}-${event.occurred_at}-${index}`}>
          <span className="timeline__when">
            <time dateTime={event.occurred_at}>{formatDateTime(event.occurred_at)}</time>
          </span>
          <span className="timeline__what">{timelineText(event)}</span>
        </li>
      ))}
    </ol>
  );
}
